import { useCallback } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

export interface DeployRun {
  id: number;
  status: 'queued' | 'in_progress' | 'completed' | string;
  conclusion: 'success' | 'failure' | 'cancelled' | 'timed_out' | 'action_required' | 'skipped' | 'neutral' | null;
  service: string | null;
  environment: string | null;
  event?: string;
  createdAt: string;
  url: string;
}

export interface WaitForRunOptions {
  service: string;
  environment: string;
  dispatchedAt: number; // epoch ms
  pollIntervalMs?: number;
  timeoutMs?: number;
  onUpdate?: (run: DeployRun | null) => void;
  // Called once per poll tick with pollsSoFar + how many runs the endpoint returned.
  // Useful for surfacing "still polling, no match yet" in the UI.
  onTick?: (info: { polls: number; totalRuns: number; error?: string }) => void;
  signal?: AbortSignal;
}

export interface WaitForRunResult {
  success: boolean;
  run: DeployRun | null;
  reason?: 'timeout' | 'aborted' | 'failure' | 'no-run' | 'error';
  error?: string;
}

// Slack window on dispatchedAt to account for GitHub API indexing lag + clock skew
const DISPATCH_SLACK_MS = 15_000;
const DEFAULT_POLL_MS = 5_000;
const DEFAULT_TIMEOUT_MS = 10 * 60_000;

export function useDeployRuns() {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  const listRuns = useCallback(async (): Promise<DeployRun[]> => {
    const baseUrl = await discoveryApi.getBaseUrl('release-manager');
    const res = await backstageFetch(`${baseUrl}/deploy-runs`);
    if (!res.ok) throw new Error(`deploy-runs failed: ${res.status}`);
    const data = await res.json();
    return data.runs ?? [];
  }, [discoveryApi, backstageFetch]);

  const findMatchingRun = useCallback(
    (runs: DeployRun[], opts: { service: string; environment: string; dispatchedAt: number }): DeployRun | null => {
      const cutoff = opts.dispatchedAt - DISPATCH_SLACK_MS;
      // Primary match: service + env + time window (works when deploy.yml has run-name)
      const exact = runs.filter((r) => {
        if (r.service !== opts.service) return false;
        if (r.environment !== opts.environment) return false;
        const created = new Date(r.createdAt).getTime();
        return created >= cutoff;
      });
      exact.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      if (exact[0]) return exact[0];

      // Fallback: newest workflow_dispatch run in window. Works when display_title
      // can't be parsed (no run-name in the workflow). Safe under sequential rollout
      // since we only have one in-flight dispatch at a time.
      const fallback = runs.filter((r) => {
        if (r.event && r.event !== 'workflow_dispatch') return false;
        const created = new Date(r.createdAt).getTime();
        return created >= cutoff;
      });
      fallback.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return fallback[0] ?? null;
    },
    [],
  );

  const waitForRun = useCallback(
    async (opts: WaitForRunOptions): Promise<WaitForRunResult> => {
      const pollMs = opts.pollIntervalMs ?? DEFAULT_POLL_MS;
      const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
      const deadline = Date.now() + timeoutMs;

      let polls = 0;
      while (Date.now() < deadline) {
        if (opts.signal?.aborted) {
          return { success: false, run: null, reason: 'aborted' };
        }

        let runs: DeployRun[] = [];
        let fetchError: string | undefined;
        try {
          runs = await listRuns();
        } catch (err: any) {
          fetchError = err?.message ?? 'deploy-runs fetch failed';
          opts.onUpdate?.(null);
        }
        polls += 1;
        opts.onTick?.({ polls, totalRuns: runs.length, error: fetchError });

        const match = findMatchingRun(runs, opts);
        opts.onUpdate?.(match);

        if (match && match.status === 'completed') {
          if (match.conclusion === 'success') {
            return { success: true, run: match };
          }
          return {
            success: false,
            run: match,
            reason: 'failure',
            error: `Workflow ${match.conclusion ?? 'failed'}`,
          };
        }

        await sleep(pollMs, opts.signal);
      }
      return { success: false, run: null, reason: 'timeout', error: `Timed out after ${timeoutMs}ms` };
    },
    [listRuns, findMatchingRun],
  );

  return { listRuns, findMatchingRun, waitForRun };
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      resolve();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
